import classNames from "classnames"
import React, { ChangeEvent, HTMLAttributes, useMemo, useRef, useState } from "react"
import * as Icons from "@nova/component-icons"
import { FileExtension } from "@nova/constants"
import {
    CollectedDropProps,
    dropConfigurationHandler,
    DroppedFile,
    fileAllowableCheck,
    FileDropPayload,
    traverseFileTree,
} from "@nova/component-upload-dropbox"
import { mapExtensionsToMimeType } from "features/job-management/uploader/constants"
import { useTranslation } from "@nova/use-translation"
import { useDrop } from "react-dnd"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"

export const UploadDropBox = ({
    title,
    canAcceptMultiple = false,
    allowedFileTypes: allowedFileExtensions,
    onNewItems,
    isProcessing,
    width = "w-[12.75rem]",
    maxFileSizeInMb,
    maxUploads = Number.POSITIVE_INFINITY,
    allowFolders,
    supportedMimeTypes,
}: {
    title: string
    canAcceptMultiple?: boolean
    allowedFileTypes: FileExtension[]
    onNewItems: (files: File[]) => Promise<boolean | void>
    isProcessing: boolean
    supportedMimeTypes: string[]
    width?: string
    maxFileSizeInMb?: number
    maxUploads?: number
    allowFolders?: boolean
} & HTMLAttributes<HTMLDivElement>) => {
    const { t } = useTranslation()

    const uploadRef = useRef<HTMLInputElement>(null)
    const uploadFolderRef = useRef<HTMLInputElement>(null)
    const [hasUploadError, setHasUploadError] = useState(false)
    const [canDrop, setCanDrop] = useState(false)
    const [isStaging, setIsStaging] = useState(false)
    const kmlProcessingText = useLoadingText(isProcessing, "Processing")

    //we need to run the file adding after a timeout so the component has a chance to re-render before the main thread gets blocked
    const handleStagingStart = (callback: () => Promise<void>) => {
        setIsStaging(true)
        setTimeout(async () => {
            await callback()
            setIsStaging(false)
        }, 0)
    }

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files: File[] = Array.from(e.target.files || [])
        handleStagingStart(() => handleNewFiles(files))
    }

    const sortAndFilterFiles = (files: File[]) => {
        const allowAll = allowedFileExtensions.includes(FileExtension.all)
        const filteredFiles = allowAll
            ? (files as DroppedFile[])
            : (fileAllowableCheck(
                  files,
                  allowedFileExtensions,
                  mapExtensionsToMimeType
              ) as DroppedFile[])

        const fileIsAllowed = filteredFiles.length !== 0
        setHasUploadError(!fileIsAllowed)
        if (!fileIsAllowed) return

        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })
        return filteredFiles.sort((a, b) =>
            collator.compare(a.fullPath || a.name, b.fullPath || b.name)
        )
    }

    const handleNewFiles = async (files: File[]) => {
        const filteredFiles = sortAndFilterFiles(files)
        if (!filteredFiles) {
            return
        }
        const newItemRes = await onNewItems(filteredFiles)
        if (newItemRes === false) setHasUploadError(true)
    }

    const handleNewFolder = async (event: React.DragEvent) => {
        if (!allowFolders) return
        event.preventDefault()
        const items = event.dataTransfer.items
        let allFiles: DroppedFile[] = []

        const isFolder = Array.from(items).some((item) => {
            const entry = item.webkitGetAsEntry?.()
            return entry && entry.isDirectory
        })
        if (!isFolder) return
        event.stopPropagation()
        for (let i = 0; i < items.length; i++) {
            const entry = items[i].webkitGetAsEntry?.()
            if (entry) {
                const files = await traverseFileTree(entry)
                allFiles = allFiles.concat(files)
            }
        }

        const filteredFiles = sortAndFilterFiles(allFiles)
        if (!filteredFiles) {
            setHasUploadError(true)
            return
        }

        onNewItems(filteredFiles)
    }

    /** For displaying which file types are allowed. */
    const fileTypeList = useMemo(
        () => allowedFileExtensions.map((type) => `${type}`).join(", "),
        [allowedFileExtensions]
    )

    /** For the accepted values in the input element. */
    const formattedFileTypes = useMemo(() => {
        if (allowedFileExtensions.includes(FileExtension.all)) return ""
        return allowedFileExtensions.map((type) => `.${type}`).join(",")
    }, [allowedFileExtensions])

    const [{ isOver }, dropRef] = useDrop<FileDropPayload, void, CollectedDropProps>(
        dropConfigurationHandler(
            (files) => handleStagingStart(() => handleNewFiles(files)),
            allowedFileExtensions,
            setHasUploadError,
            mapExtensionsToMimeType,
            undefined,
            maxUploads
        ),
        [handleNewFiles, allowedFileExtensions, setHasUploadError]
    )

    const ref = useRef(null)
    dropRef(ref)

    return (
        <div
            ref={ref}
            className={classNames(
                "relative py-6 px-2 border rounded-md flex flex-col items-center justify-center text-center",
                {
                    "border-dashed": !hasUploadError,
                    "border-red-500": hasUploadError || (isOver && !canDrop),
                    "border-white-500": !hasUploadError && !isOver,
                    "border-blue-500": isOver && canDrop,
                }
            )}
            style={{ width }}
            onDragEnter={(e) => {
                e.preventDefault()
                if (allowedFileExtensions.includes(FileExtension.all)) {
                    setCanDrop(true)
                    return
                }
                const items = e.dataTransfer?.items
                if (items && items.length < maxUploads && items[0]?.kind === "file") {
                    const { type } = items[0]
                    if (supportedMimeTypes.includes(type) || !type) {
                        setCanDrop(true)
                        return
                    }
                }

                setCanDrop(false)
            }}
            onDrop={(e) => handleNewFolder(e)}
        >
            {!isProcessing && !isStaging ? (
                <>
                    <Icons.Icon Icon={Icons.Backup} className="text-gray-0 mb-2" />
                    <h4 className="text-gray-0 mb-2">
                        {t(hasUploadError ? "Incorrect File Type" : title)}
                    </h4>
                    <h5 className="mb-1">
                        <span
                            className="text-blue-200 hover:text-blue-500 underline cursor-pointer"
                            onClick={() => uploadRef.current?.click()}
                        >
                            {t(canAcceptMultiple ? "Choose files" : "Choose a file")}
                        </span>
                        {allowFolders && (
                            <>
                                <span className="text-gray-400">{t(` or `)}</span>
                                <span
                                    className="text-blue-200 hover:text-blue-500 underline cursor-pointer"
                                    onClick={() => uploadFolderRef.current?.click()}
                                >
                                    folders
                                </span>
                            </>
                        )}
                        <span className="text-gray-400">
                            {t(` or drag ${canAcceptMultiple ? "them" : "it"} here`)}
                        </span>
                    </h5>
                    {!isOver && (
                        <>
                            <h5 className="text-gray-400">{fileTypeList}</h5>
                            {maxFileSizeInMb && (
                                <h5 className="text-gray-400">
                                    {t(`Max file size: ${maxFileSizeInMb}mb`)}
                                </h5>
                            )}
                        </>
                    )}
                    <input
                        ref={uploadRef}
                        type="file"
                        multiple={canAcceptMultiple}
                        accept={formattedFileTypes}
                        onChange={handleInputChange}
                        className="hidden"
                    />
                    {allowFolders && (
                        <>
                            <input
                                ref={uploadFolderRef}
                                type="file"
                                multiple={canAcceptMultiple}
                                onChange={handleInputChange}
                                className="hidden"
                                // @ts-expect-error this is the supported way to allow folders
                                webkitdirectory="true"
                            />
                        </>
                    )}
                </>
            ) : (
                <div className="px-2 my-9 relative">
                    <NovaSpinner width={24} height={24} />
                    <h4 className="text-gray-0 mt-1">{kmlProcessingText}</h4>
                </div>
            )}
        </div>
    )
}
